/**
 * build-pc.js — CompuMe Build Your Gaming PC v2.0
 * 2025-06: CREATED_AT→CREATED fix, brand interleave, monitor selector, blueprint locale
 * v2.3 share: bpRenderBlueprint applies BP._shared_tier_id after tiers load
 * v2.4 handoff: detects [CONTACT_HANDOFF_REQUESTED] in AI reply and renders WA CTA
 * v2.5 build_context: bpAiSend now forwards wizard state to Worker
 * v2.6 finalize_build: handles finalize_build/redirect_to_cart actions from Worker;
 *      defensive UI for [BUILD_CONFIRMED]/[FINALIZE_BUILD_READY] without action;
 *      variant GID type validation; [PICK:] marker detection.
 */
'use strict';

const BP = {
  stage:1, useCase:null,
  resolution:'1080p',
  monitor_target:null,
  refresh_target:null,
  monitor_tier:null,
  budget:900, tier:null,
  _shared_tier_id: null,
  build:{cpu:null,motherboard:null,ram:null,gpu:null,psu:null,storage:null,case:null,cooler:null,fans:null,thermalpaste:null,os:null,warranty:null,build_fee:null},
  aiMessages:[],
  flash:{offered:false,claimed:false,code:null,expiresAt:null,timer:null,msgCount:0,skipped:false},
  AI_URL:'https://pc-builder-ai.bilal-894.workers.dev/'
};
const BP_REQUIRED=['cpu','motherboard','ram','gpu','psu','storage','case','cooler'];
const BP_CATEGORIES=[
  {key:'cpu',         label:'CPU',           col:'pc-builder-cpu',         icon:'\u26a1', req:true},
  {key:'motherboard', label:'Motherboard',   col:'pc-builder-motherboard', icon:'\ud83d\udd0c', req:true},
  {key:'ram',         label:'RAM',           col:'pc-builder-ram',         icon:'\ud83d\udcbe', req:true},
  {key:'gpu',         label:'GPU',           col:'pc-builder-gpu',         icon:'\ud83c\udfae', req:true},
  {key:'psu',         label:'Power Supply',  col:'pc-builder-psu',         icon:'\ud83d\udd0b', req:true},
  {key:'storage',     label:'Storage',       col:'pc-builder-storage',     icon:'\ud83d\udcbf', req:true},
  {key:'case',        label:'Case',          col:'pc-builder-case',        icon:'\ud83d\udda5\ufe0f', req:true},
  {key:'cooler',      label:'CPU Cooler',    col:'pc-builder-cooler',      icon:'\u2744\ufe0f', req:true},
  {key:'fans',        label:'Case Fans',     col:'pc-builder-fans',        icon:'\ud83c\udf00', req:false},
  {key:'thermalpaste',label:'Thermal Paste', col:'pc-builder-thermalpaste',icon:'\ud83e\uddea', req:false},
];
const BP_SERVICES=[
  {key:'os',       label:'Operating System',col:'pc-builder-os',       icon:'\ud83d\udcbb'},
  {key:'build_fee',label:'Build & Assembly',col:'pc-builder-build-fee',icon:'\ud83d\udd27'},
  {key:'warranty', label:'Warranty',        col:'pc-builder-warranty', icon:'\ud83d\udee1\ufe0f'},
];
const FF_RANK={ITX:1,mITX:1,mATX:2,MicroATX:2,ATX:3,EATX:4};
const BP_BUDGET_ALLOC={
  cpu:0.18, gpu:0.35, motherboard:0.10, ram:0.07,
  storage:0.05, psu:0.07, case:0.10, cooler:0.08,
  fans:0.05, thermalpaste:0.02
};
function bpBudgetCeiling(catKey){return Math.round((BP_BUDGET_ALLOC[catKey]||0.10)*BP.budget*1.5);}
const BP_MF_CACHE={};

function gidToNum(gid){
  if(!gid)return null;
  const s=String(gid);
  if(s.includes('/'))return parseInt(s.split('/').pop(),10);
  return parseInt(s,10);
}

/*
 * bpIsVariantGid(gid)
 * Returns true only for ProductVariant GIDs or plain numeric IDs.
 * Explicitly rejects Product GIDs to prevent silent mis-cart.
 */
function bpIsVariantGid(gid) {
  if (!gid) return false;
  const s = String(gid);
  // Reject product GIDs (gid://shopify/Product/...)
  if (/gid:\/\/shopify\/Product\/\d+$/.test(s)) return false;
  // Accept variant GIDs or plain numbers
  return /gid:\/\/shopify\/ProductVariant\/\d+$/.test(s) || /^\d+$/.test(s);
}

/* ─── Monitor labels (cart attributes) ──────────────────────────────────── */
const BP_MONITOR_LABELS = {
  'esports_1080p_high_refresh': 'Esports Entry',
  'balanced_1440p':             'Balanced Gaming',
  'qhd_240hz':                  'High Refresh QHD',
  '4k_cinematic':               '4K Cinematic',
  'premium_oled_ultrawide':     'Premium OLED / UltraWide',
};

/* ─── Resolution → monitor_target fallback (old saved hashes) ───────────── */
const BP_RESOLUTION_FALLBACK = {
  '1080p': { monitor_target:'esports_1080p_high_refresh', refresh_target:240, monitor_tier:'entry_esports' },
  '1440p': { monitor_target:'balanced_1440p',             refresh_target:165, monitor_tier:'mid_balanced'  },
  '4K':    { monitor_target:'4k_cinematic',               refresh_target:144, monitor_tier:'high_4k'       },
  '4k':    { monitor_target:'4k_cinematic',               refresh_target:144, monitor_tier:'high_4k'       },
};
function bpApplyMonitorFallback() {
  if (BP.monitor_target) return;
  const fb = BP_RESOLUTION_FALLBACK[BP.resolution]; if(!fb)return;
  BP.monitor_target = fb.monitor_target;
  BP.refresh_target = fb.refresh_target;
  BP.monitor_tier   = fb.monitor_tier;
  const card = document.querySelector(`[data-monitor-target="${BP.monitor_target}"]`);
  if (card) {
    document.querySelectorAll('.bp-monitor-card').forEach(el=>{el.classList.remove('bp-selected');el.setAttribute('aria-pressed','false');});
    card.classList.add('bp-selected'); card.setAttribute('aria-pressed','true');
  }
}
window.bpApplyMonitorFallback = bpApplyMonitorFallback;

function bpIsAr() { return (window.BP_LOCALE || 'en') === 'ar'; }

/* ═══════════════════════════════════════════════════════
   CONTACT HANDOFF — v2.4
═══════════════════════════════════════════════════════ */
const BP_WA_HANDOFF_NUMBER = '962798000180';

function bpCheckHandoff(replyText, logEl) {
  if (!replyText || !logEl) return;
  if (!replyText.includes('[CONTACT_HANDOFF_REQUESTED]')) return;
  bpRenderHandoffCTA(logEl);
}

function bpRenderHandoffCTA(logEl) {
  const ar = bpIsAr();
  const baseMsg = ar
    ? '\u0645\u0631\u062d\u0628\u0627\u064b CompuMe\u060c \u0623\u062d\u062a\u0627\u062c \u0645\u0633\u0627\u0639\u062f\u0629 \u0628\u062e\u0635\u0648\u0635 \u062a\u0643\u0648\u064a\u0646 \u062c\u0647\u0627\u0632 \u0627\u0644\u0643\u0645\u0628\u064a\u0648\u062a\u0631.'
    : 'Hello CompuMe, I need help with my PC Builder request.';
  let shareUrl = null;
  try { if (typeof bpGenerateShareURL === 'function') shareUrl = bpGenerateShareURL(); } catch(e) {}
  let waMsg = baseMsg;
  if (shareUrl) {
    waMsg += ar
      ? '\n\u0631\u0627\u0628\u0637 \u0627\u0644\u062a\u0643\u0648\u064a\u0646: ' + shareUrl
      : '\nBuild link: ' + shareUrl;
  }
  const waURL = 'https://wa.me/' + BP_WA_HANDOFF_NUMBER + '?text=' + encodeURIComponent(waMsg);
  const label  = ar ? '\u062a\u0648\u0627\u0635\u0644 \u0645\u0639 CompuMe \u0639\u0644\u0649 \u0648\u0627\u062a\u0633\u0627\u0628' : 'Chat with CompuMe on WhatsApp';
  const helper = ar ? '\u0641\u0631\u064a\u0642\u0646\u0627 \u064a\u0633\u062a\u0637\u064a\u0639 \u0645\u062a\u0627\u0628\u0639\u0629 \u0627\u0644\u0637\u0644\u0628 \u0639\u0644\u0649 \u0648\u0627\u062a\u0633\u0627\u0628.' : 'Our team can continue this request on WhatsApp.';
  const card = document.createElement('div');
  card.className = 'bp-handoff-card';
  card.setAttribute('dir', ar ? 'rtl' : 'ltr');
  card.innerHTML =
    `<p class="bp-handoff-helper">${helper}</p>` +
    `<a class="bp-handoff-wa-btn" href="${waURL}" target="_blank" rel="noopener noreferrer">` +
      `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style="flex-shrink:0">` +
        `<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>` +
      `</svg>` +
      `<span>${label}</span>` +
    `</a>`;
  logEl.appendChild(card);
  logEl.scrollTop = logEl.scrollHeight;
  if (!document.getElementById('bp-handoff-css')) {
    const s = document.createElement('style'); s.id = 'bp-handoff-css';
    s.textContent = [
      '.bp-handoff-card{margin-top:10px;padding:14px 16px;background:rgba(37,211,102,0.06);border:1px solid rgba(37,211,102,0.3);border-radius:8px;}',
      '.bp-handoff-helper{font-family:var(--bp-font-mono,"DM Mono",monospace);font-size:12px;color:rgba(37,211,102,0.75);margin:0 0 10px;}',
      '.bp-handoff-wa-btn{display:inline-flex;align-items:center;gap:9px;background:#25D366;color:#fff;font-family:var(--bp-font-display,"Barlow Condensed",sans-serif);font-weight:700;font-size:14px;letter-spacing:0.04em;text-transform:uppercase;text-decoration:none;padding:10px 18px;border-radius:6px;transition:background 0.15s,transform 0.15s;}',
      '.bp-handoff-wa-btn:hover{background:#1ebe5c;transform:translateY(-1px);}',
    ].join('');
    document.head.appendChild(s);
  }
}

/* ═══════════════════════════════════════════════════════
   FINALIZE BUILD — v2.6
   Handles finalize_build action from Worker.
   Called from bpAiSend after receiving data.actions.
   Validates variant IDs before attempting cart add.
   Shows explicit error on bad GID type or cart failure.
   Never redirects before Shopify cart add succeeds.
═══════════════════════════════════════════════════════ */
async function bpHandleFinalizeBuild(action, log) {
  const items = Array.isArray(action.items) ? action.items : [];

  // Validate: must have at least one item
  if (!items.length) {
    bpAiAppend('bot', '> [CART_ERROR] Cart action missing items. Please reselect your components or contact CompuMe.');
    return;
  }

  // Validate: all items must be variant GIDs, not product GIDs
  const badItems = items.filter(item => !bpIsVariantGid(item.variant_id || item.id));
  if (badItems.length) {
    console.error('[BP Finalize] Invalid GID type in action items:', badItems);
    bpAiAppend('bot',
      '> [CART_ERROR] Cart add failed: missing variant ID.\n' +
      '> The AI provided a product ID instead of a variant ID.\n' +
      '> Please use the \'Review Build\' stage to add items to cart, or contact CompuMe.');
    return;
  }

  // Build cart items array from action
  const cartItems = items.map(item => ({
    id: gidToNum(item.variant_id || item.id),
    quantity: item.quantity || 1
  })).filter(i => i.id && !isNaN(i.id));

  if (!cartItems.length) {
    bpAiAppend('bot', '> [CART_ERROR] Cart add failed: variant IDs could not be resolved. Please use the Review Build stage.');
    return;
  }

  // Show in-progress message
  const tid = 'bp-finalize-' + Date.now();
  bpAiAppend('bot', '> [ADDING_TO_CART] Adding your build to cart...', tid);

  try {
    const monitorLabel = BP_MONITOR_LABELS[BP.monitor_target] || BP.monitor_target || '';
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: cartItems,
        attributes: {
          'Build Type':     'Custom PC Builder (AI)',
          'BP_UseCase':     BP.useCase     || '',
          'BP_Budget':      String(BP.budget),
          'BP_Monitor':     monitorLabel,
          'BP_Resolution':  BP.resolution  || ''
        }
      })
    });

    if (!r.ok) {
      let errMsg = 'Cart add failed (' + r.status + ')';
      try { const e = await r.json(); errMsg = e.description || e.message || JSON.stringify(e); } catch(_) {}
      console.error('[BP Finalize] /cart/add.js error:', errMsg);
      // Replace the in-progress message
      const el = document.getElementById(tid); if (el) el.id = '';
      bpAiAppend('bot', '> [CART_ERROR] ' + errMsg + '\n> Please try the \'Review Build\' button or contact CompuMe.');
      return;
    }

    const added = await r.json();
    console.log('[BP Finalize] Cart add success:', added.items?.length, 'items');
    // Replace in-progress message with success
    const el = document.getElementById(tid); if (el) el.id = '';
    bpAiAppend('bot', '> [CART_READY] \u2714 Build added to cart. Redirecting...');

    // Only redirect after confirmed success
    setTimeout(() => {
      if (BP.flash.claimed && BP.flash.code) {
        window.location.href = '/discount/' + BP.flash.code + '?redirect=' + encodeURIComponent('/cart');
      } else {
        window.location.href = '/cart';
      }
    }, 800);

  } catch(e) {
    const el = document.getElementById(tid); if (el) el.id = '';
    console.error('[BP Finalize] fetch error:', e);
    bpAiAppend('bot', '> [CART_ERROR] Connection failed: ' + e.message + '\n> Please try the \'Review Build\' button or contact CompuMe.');
  }
}

/* ─── Brand interleave ───────────────────────────────────────────────────── */
const BP_BRAND_HINTS = {
  cpu:         ['Intel','AMD'],
  gpu:         ['NVIDIA','AMD','ASUS','MSI','Gigabyte','Zotac','EVGA','Sapphire','PowerColor'],
  motherboard: ['ASUS','MSI','Gigabyte','ASRock'],
  ram:         ['G.SKILL','Kingston','Corsair','Samsung','Crucial','TeamGroup'],
  storage:     ['Samsung','Kingston','Silicon Power','Western Digital','Seagate','Crucial','Lexar','SanDisk'],
  psu:         ['Corsair','EVGA','Seasonic','be quiet!','Thermaltake','Cooler Master','MSI','Asus'],
  case:        ['NZXT','Lian Li','Fractal Design','Corsair','be quiet!','Cooler Master','Phanteks','SilverStone'],
  cooler:      ['Noctua','be quiet!','Cooler Master','Arctic','NZXT','Corsair','Thermalright','DeepCool'],
  fans:        ['Noctua','be quiet!','Corsair','ARCTIC','Lian Li','Phanteks'],
  thermalpaste:['Thermal Grizzly','Arctic','Noctua','Corsair']
};
function bpGuessBrand(product, catKey) {
  const mf = BP_MF_CACHE[product.id];
  if (mf && mf.brand_name) return mf.brand_name;
  const title = (product.title || '').toLowerCase();
  const hints = BP_BRAND_HINTS[catKey] || [];
  for (const b of hints) { if (title.includes(b.toLowerCase())) return b; }
  return (product.title || '').split(' ')[0] || 'Other';
}
function bpInterleaveByBrand(products, catKey) {
  if (!products || products.length <= 1) return products;
  const brandMap = new Map();
  for (const p of products) {
    const brand = bpGuessBrand(p, catKey);
    if (!brandMap.has(brand)) brandMap.set(brand, []);
    brandMap.get(brand).push(p);
  }
  const brandQueues = Array.from(brandMap.values());
  brandQueues.sort((a, b) =>
    parseFloat(a[0].priceRange?.minVariantPrice?.amount || 0) -
    parseFloat(b[0].priceRange?.minVariantPrice?.amount || 0)
  );
  const result = []; let remaining = true;
  while (remaining) {
    remaining = false;
    for (const queue of brandQueues) {
      if (queue.length > 0) { result.push(queue.shift()); if (queue.length > 0) remaining = true; }
    }
  }
  return result;
}

document.addEventListener('DOMContentLoaded',()=>{
  bpRestoreFromHash();
  bpApplyMonitorFallback();
  const slider=document.getElementById('bp-budget-slider');
  if(slider){slider.value=BP.budget;bpUpdateBudget(slider);}
  bpRefreshBar();
});

/* ═══════════════════════════════════════════════════════
   STAGE 1
═══════════════════════════════════════════════════════ */
function bpSelectUseCase(btn){
  document.querySelectorAll('.bp-use-case-card').forEach(el=>{el.classList.remove('bp-selected');el.setAttribute('aria-pressed','false');});
  btn.classList.add('bp-selected'); btn.setAttribute('aria-pressed','true');
  BP.useCase = btn.dataset.useCase;
  bpCheckWizardReady();
}
function bpSelectMonitor(btn){
  document.querySelectorAll('.bp-monitor-card').forEach(el=>{el.classList.remove('bp-selected');el.setAttribute('aria-pressed','false');});
  btn.classList.add('bp-selected'); btn.setAttribute('aria-pressed','true');
  BP.resolution     = btn.dataset.res    || '1080p';
  BP.monitor_target = btn.dataset.monitorTarget || null;
  BP.refresh_target = btn.dataset.refresh ? parseInt(btn.dataset.refresh, 10) : null;
  BP.monitor_tier   = btn.dataset.monitorTier  || null;
  bpCheckWizardReady();
}
window.bpSelectMonitor = bpSelectMonitor;
function bpSelectRes(btn){ bpSelectMonitor(btn); }
function bpUpdateBudget(input){
  BP.budget=parseInt(input.value,10);
  const el=document.getElementById('bp-budget-display');
  if(el)el.textContent=BP.budget.toLocaleString();
  const pct=((BP.budget-300)/4700*100).toFixed(1);
  input.style.background=`linear-gradient(to right,var(--bp-cyan) ${pct}%,var(--bp-s4) ${pct}%)`;
  bpCheckWizardReady();
}
function bpCheckWizardReady(){
  const btn=document.getElementById('bp-show-build-btn'); if(!btn)return;
  const ready = !!(BP.useCase && BP.monitor_target);
  btn.disabled = !ready; btn.setAttribute('aria-disabled', String(!ready));
  bpRefreshBar();
}
function bpProceedToBlueprint(){
  if(!BP.useCase || !BP.monitor_target) return;
  bpGoToStage(2); bpRenderBlueprint();
}

// NAVIGATION
function bpGoToStage(n){
  BP.stage=n;
  for(let i=1;i<=4;i++){const el=document.getElementById('bp-stage-'+i);if(el)el.classList.toggle('bp-stage--active',i===n);}
  ['1','2','3','4'].forEach(i=>{const nav=document.getElementById('bp-nav-'+i);if(!nav)return;const num=parseInt(i);nav.classList.remove('active','done');if(num===n)nav.classList.add('active');if(num<n)nav.classList.add('done');});
  bpRefreshBar();bpSaveToHash();window.scrollTo({top:0,behavior:'smooth'});
  if(n===4)setTimeout(bpCheckFlashEligibility,800);
}
function bpNextStage(){const next=BP.stage+1;if(next>4)return;bpGoToStage(next);if(next===2)bpRenderBlueprint();if(next===3)bpRenderCustomize();if(next===4)bpRenderReview();}

/* ═══════════════════════════════════════════════════════
   STAGE 2 — Blueprint
═══════════════════════════════════════════════════════ */
async function bpRenderBlueprint(){
  const container=document.getElementById('bp-stage-2');if(!container)return;
  const ar = bpIsAr();
  const headline  = ar ? '\u0627\u0644\u062e\u0637\u0629 \u0627\u0644\u0645\u0642\u062a\u0631\u062d\u0629' : 'Your Blueprint';
  const subhead   = ar ? '\u0627\u062e\u062a\u0631 \u0646\u0642\u0637\u0629 \u0627\u0644\u0628\u062f\u0627\u064a\u0629 \u2014 \u0648\u064a\u0645\u0643\u0646\u0643 \u062a\u0639\u062f\u064a\u0644 \u0643\u0644 \u0642\u0637\u0639\u0629 \u0641\u064a \u0627\u0644\u062e\u0637\u0648\u0629 \u0627\u0644\u062a\u0627\u0644\u064a\u0629.' : 'Choose a starting point \u2014 you can customize every part next.';
  const changeBtn = ar ? '\u062a\u063a\u064a\u064a\u0631 \u0627\u0644\u0627\u0633\u062a\u062e\u062f\u0627\u0645 \u2190' : '\u2190 Change Use Case';
  container.innerHTML='<div class="bp-loading"><div class="bp-spinner"></div> ' + (ar ? '\u062c\u0627\u0631\u064a \u0627\u0644\u062a\u062d\u0645\u064a\u0644...' : 'Loading tiers...') + '</div>';
  let tiers=[];
  try{
    const r=await fetch(window.BP_TIER_PRESETS_URL);
    tiers=(await r.json()).tiers||[];
  }catch(e){
    container.innerHTML='<p style="color:var(--bp-red);font-family:var(--bp-font-mono);padding:20px">> ERROR: Could not load tiers.</p>';
    return;
  }
  window._BP_TIERS=tiers;
  const relevant=tiers.filter(t=>BP.budget>=t.budget_min-150&&BP.budget<=t.budget_max+600);
  const display=relevant.length?relevant:tiers;
  container.innerHTML=`
    <h2 class="bp-headline" style="font-size:clamp(1.8rem,4vw,3rem)">${headline}</h2>
    <p class="bp-subhead">${subhead}</p>
    <div class="bp-tier-grid">${display.map(t=>{
      const name      = t.name;
      const range     = `${t.budget_min.toLocaleString()}\u2013${t.budget_max.toLocaleString()} JOD`;
      const targetFps = ar && t.target_fps_ar ? t.target_fps_ar : t.target_fps;
      const specs     = ar && t.specs_ar ? t.specs_ar : t.specs;
      const bestForLabel = t.best_for ? (ar ? t.best_for_ar || t.best_for : 'Best for: ' + t.best_for) : '';
      return `
      <div class="bp-tier-card" data-tier-id="${t.id}" style="--bp-tier-color:${t.color}"
        onclick="bpSelectTier(this)" role="button" tabindex="0" aria-pressed="false"
        onkeydown="if(event.key==='Enter'||event.key===' ')this.click()">
        <div class="bp-tier-name">${name}</div>
        <div class="bp-tier-range">${range} &middot; <span style="color:var(--bp-muted);font-size:0.85em">${targetFps}</span></div>
        <ul class="bp-tier-specs">${specs.map(s=>`<li>${s}</li>`).join('')}</ul>
        ${bestForLabel ? `<div class="bp-tier-bestfor">${bestForLabel}</div>` : ''}
      </div>`;
    }).join('')}</div>
    <button class="bp-btn bp-btn--ghost" onclick="bpGoToStage(1)" type="button">${changeBtn}</button>`;
  if (!document.getElementById('bp-tier-bestfor-style')) {
    const s = document.createElement('style'); s.id = 'bp-tier-bestfor-style';
    s.textContent = '.bp-tier-bestfor{margin-top:10px;padding-top:8px;border-top:1px solid rgba(255,255,255,0.07);font-family:var(--bp-font-mono);font-size:11px;color:var(--bp-green);opacity:0.85;letter-spacing:0.04em;}';
    document.head.appendChild(s);
  }
  if (BP._shared_tier_id) {
    const sharedCard = container.querySelector(`[data-tier-id="${BP._shared_tier_id}"]`);
    if (sharedCard) { BP._shared_tier_id = null; sharedCard.click(); return; }
    BP._shared_tier_id = null;
  }
  bpRefreshBar();
}
function bpSelectTier(card){document.querySelectorAll('.bp-tier-card').forEach(el=>{el.classList.remove('bp-selected');el.setAttribute('aria-pressed','false');});card.classList.add('bp-selected');card.setAttribute('aria-pressed','true');BP.tier=(window._BP_TIERS||[]).find(t=>t.id===card.dataset.tierId)||{id:card.dataset.tierId};bpRefreshBar();setTimeout(()=>{bpGoToStage(3);bpRenderCustomize();},380);}

// STAGE 3
function bpRenderCustomize(){
  const container=document.getElementById('bp-stage-3');if(!container)return;
  container.innerHTML=`
    <h2 class="bp-headline" style="font-size:clamp(1.8rem,4vw,3rem)">Pick Your Parts</h2>
    <p class="bp-subhead">Select one from each category. Compatibility checked live.</p>
    <div id="bp-compat-alerts"></div>
    <div id="bp-catalog"></div>
    <div style="display:flex;gap:12px;flex-wrap:wrap;margin-top:40px;padding-top:32px;border-top:1px solid rgba(0,212,255,0.15)">
      <button class="bp-btn bp-btn--ghost" onclick="bpGoToStage(2)" type="button">&larr; Change Tier</button>
      <button class="bp-btn bp-btn--primary" onclick="bpGoToStage(4);bpRenderReview()" type="button">Review Build &rarr;</button>
    </div>`;
  const catalog=document.getElementById('bp-catalog');
  BP_CATEGORIES.forEach(cat=>{
    const selected=BP.build[cat.key];
    const section=document.createElement('div');section.className='bp-cat-section';
    section.innerHTML=`
      <div class="bp-cat-hdr">
        <div class="bp-cat-hdr-left">
          <span class="bp-cat-icon">${cat.icon}</span>
          <span class="bp-cat-title">${cat.label}</span>
          ${!cat.req?'<span class="bp-cat-optional">Optional</span>':''}
        </div>
        <div class="bp-cat-status" id="bp-cat-status-${cat.key}">
          ${selected?`<span class="bp-cat-selected-badge">\u2713 ${selected.title} \u2014 JOD ${(selected.price||0).toFixed(2)}</span>`:'<span class="bp-cat-unselected">Not selected yet</span>'}
        </div>
      </div>
      <div class="bp-prod-grid" id="bp-cat-${cat.key}">
        ${[1,2,3,4,5,6].map(()=>`<div class="bp-skel"><div class="bp-skel-img"></div><div class="bp-skel-body"><div class="bp-skel-line w80"></div><div class="bp-skel-line w70"></div><div class="bp-skel-line w50"></div></div></div>`).join('')}
      </div>`;
    catalog.appendChild(section);
  });
  BP_CATEGORIES.forEach(cat=>bpLoadCatalogSection(cat));
}

// SPEC BADGES
function bpExtractSpecs(title,catKey){
  const t=title||'';const badges=[];
  if(catKey==='ram'){const cap=t.match(/(\d+)\s*GB/i)?.[1];const spd=t.match(/(\d+)\s*MT\/s/i)?.[1]||t.match(/(\d+)\s*M[Hh]z/i)?.[1];const ddr=t.match(/DDR(\d)/i)?.[1];if(cap)badges.push({text:cap+'GB',cls:'cyan'});if(ddr)badges.push({text:'DDR'+ddr,cls:'purple'});if(spd)badges.push({text:spd+'MHz',cls:'amber'});}
  else if(catKey==='storage'){const tb=t.match(/(\d+(?:\.\d+)?)\s*TB/i)?.[1];const gb=!tb?t.match(/(\d+)\s*GB/i)?.[1]:null;const nvme=/NVMe|M\.2/i.test(t);const sata=/SATA/i.test(t);if(tb)badges.push({text:tb+'TB',cls:'cyan'});else if(gb)badges.push({text:gb+'GB',cls:'cyan'});if(nvme)badges.push({text:'NVMe',cls:'green'});else if(sata)badges.push({text:'SATA',cls:'amber'});}
  else if(catKey==='psu'){const w=t.match(/(\d{3,4})\s*W/i)?.[1];const plat=/Platinum/i.test(t);const gold=/\bGold\b/i.test(t);const bron=/Bronze/i.test(t);const fullmod=/Full[\s-]?Modular/i.test(t);const semimod=/Semi[\s-]?Modular/i.test(t);if(w)badges.push({text:w+'W',cls:'cyan'});if(plat)badges.push({text:'Platinum',cls:'purple'});else if(gold)badges.push({text:'Gold',cls:'amber'});else if(bron)badges.push({text:'Bronze',cls:'orange'});if(fullmod)badges.push({text:'Full-Mod',cls:'green'});else if(semimod)badges.push({text:'Semi-Mod',cls:'green'});}
  else if(catKey==='cpu'){const brand=/AMD|Ryzen/i.test(t)?'AMD':/Intel/i.test(t)?'Intel':null;const cores=t.match(/(\d+)[\s-]?Core/i)?.[1];if(brand)badges.push({text:brand,cls:brand==='AMD'?'red':'blue'});if(cores)badges.push({text:cores+'-Core',cls:'cyan'});}
  else if(catKey==='gpu'){const vram=t.match(/(\d+)\s*GB/i)?.[1];const rtx=t.match(/RTX\s*(\d{4}(?:\s*Ti)?)/i)?.[1];const gtx=t.match(/GTX\s*(\d{4}(?:\s*Ti)?)/i)?.[1];const rx=t.match(/RX\s*(\d{4}(?:\s*XT)?)/i)?.[1];if(vram)badges.push({text:vram+'GB VRAM',cls:'cyan'});if(rtx)badges.push({text:'RTX '+rtx.replace(/\s+/,''),cls:'green'});else if(gtx)badges.push({text:'GTX '+gtx.replace(/\s+/,''),cls:'amber'});else if(rx)badges.push({text:'RX '+rx.replace(/\s+/,''),cls:'red'});}
  else if(catKey==='motherboard'){const form=t.match(/\b(ATX|mATX|Micro-?ATX|Mini-?ITX|mITX|E-?ATX)\b/i)?.[1];const ddr=t.match(/DDR(\d)/i)?.[1];const sock=t.match(/\b(AM5|AM4|LGA\d{4})\b/i)?.[1];if(form)badges.push({text:form.replace(/micro-?/i,'m').replace(/mini-?/i,'m-'),cls:'cyan'});if(sock)badges.push({text:sock,cls:'amber'});if(ddr)badges.push({text:'DDR'+ddr,cls:'purple'});}
  else if(catKey==='cooler'){const mm=t.match(/(\d{3})\s*mm/i)?.[1];const aio=/(AIO|Liquid)/i.test(t);const air=/(Air|Tower)/i.test(t)&&!/(AIO|Liquid)/i.test(t);if(mm)badges.push({text:mm+'mm AIO',cls:'blue'});else if(aio)badges.push({text:'AIO Liquid',cls:'blue'});else if(air)badges.push({text:'Air Cooler',cls:'cyan'});}
  return badges;
}

async function bpFetchProducts(handle,limit,sortKey){
  const sortArg=sortKey?`,sortKey:${sortKey}`:',sortKey:COLLECTION_DEFAULT';
  const q=`{
    collection(handle:"${handle}"){
      products(first:${limit}${sortArg}){
        edges{node{
          id title handle
          priceRange{minVariantPrice{amount}}
          featuredImage{url altText}
          variants(first:1){edges{node{id availableForSale}}}
        }}
      }
    }
  }`;
  const r=await fetch(window.BP_API,{method:'POST',headers:{'Content-Type':'application/json','X-Shopify-Storefront-Access-Token':window.BP_STOREFRONT_TOKEN},body:JSON.stringify({query:q})});
  const data=await r.json();
  if(data.errors){console.error('[BP:'+handle+'] Storefront errors:',JSON.stringify(data.errors,null,2));}
  return (data?.data?.collection?.products?.edges||[]).map(e=>e.node);
}

async function bpLoadCatalogSection(cat){
  const grid=document.getElementById('bp-cat-'+cat.key);if(!grid)return;
  let prods=[];
  try{
    prods=await bpFetchProducts(cat.col,48,'PRICE');
    console.log('[BP:'+cat.key+'] PRICE \u2192',prods.length);
    if(!prods.length){console.warn('[BP:'+cat.key+'] retry COLLECTION_DEFAULT');prods=await bpFetchProducts(cat.col,48,null);console.log('[BP:'+cat.key+'] COLLECTION_DEFAULT \u2192',prods.length);}
    if(!prods.length){console.warn('[BP:'+cat.key+'] retry CREATED');prods=await bpFetchProducts(cat.col,48,'CREATED');console.log('[BP:'+cat.key+'] CREATED \u2192',prods.length);}
  }catch(e){
    console.error('[BP:'+cat.key+']',e);
    grid.innerHTML=`<p style="color:#FF2255;font-family:DM Mono,monospace;font-size:13px;padding:16px 0">> ERROR: ${e.message}</p>`;
    return;
  }
  if(!prods.length){grid.innerHTML='<p style="color:#5a5a7a;font-size:13px;padding:16px 0;font-family:DM Mono,monospace">No products found.</p>';return;}
  const interleaved = bpInterleaveByBrand(prods, cat.key);
  const ceiling=bpBudgetCeiling(cat.key);
  const inBudget=interleaved.filter(p=>parseFloat(p.priceRange?.minVariantPrice?.amount||0)<=ceiling);
  const overBudget=interleaved.filter(p=>parseFloat(p.priceRange?.minVariantPrice?.amount||0)>ceiling);
  const display=inBudget.length>0?inBudget:interleaved.slice(0,5);
  grid.innerHTML=display.map(p=>bpProductCard(p,cat)).join('');
  if(overBudget.length>0&&inBudget.length>0){
    window['_bpMore_'+cat.key]=overBudget;
    const wrap=document.createElement('div');wrap.id='bp-more-wrap-'+cat.key;wrap.style.cssText='grid-column:1/-1;padding:8px 0 4px;';
    wrap.innerHTML=`<button class="bp-show-more-btn" onclick="bpShowMoreProducts('${cat.key}')" type="button">\u25bc &nbsp;Show ${overBudget.length} higher-priced options</button>`;
    grid.appendChild(wrap);
  }
  bpEnrichMetafields(cat);
}

function bpShowMoreProducts(key){
  const moreProds=window['_bpMore_'+key]||[];const cat=BP_CATEGORIES.find(c=>c.key===key);const grid=document.getElementById('bp-cat-'+key);
  if(!grid||!cat||!moreProds.length)return;
  const wrap=document.getElementById('bp-more-wrap-'+key);if(wrap)wrap.remove();
  grid.insertAdjacentHTML('beforeend',moreProds.map(p=>bpProductCard(p,cat)).join(''));
}

function bpProductCard(p,cat){
  const price=parseFloat(p.priceRange?.minVariantPrice?.amount||0);
  const img=p.featuredImage?.url||'';
  const alt=p.featuredImage?.altText||p.title;
  const inStock=p.variants?.edges?.[0]?.node?.availableForSale!==false;
  const varId=gidToNum(p.variants?.edges?.[0]?.node?.id);
  const isSel=BP.build[cat.key]?.id===p.id;
  const mf=BP_MF_CACHE[p.id]||{};
  const prodData={id:p.id,title:p.title,handle:p.handle,price,image:img,variantId:varId,cpu_socket:mf.cpu_socket||null,tdp_watts:mf.tdp_watts||null,form_factor:mf.form_factor||null,ram_type:mf.ram_type||null,ram_speed_mhz:mf.ram_speed_mhz||null,psu_wattage:mf.psu_wattage||null,cooler_type:mf.cooler_type||null};
  const pd=JSON.stringify(prodData).replace(/"/g,'&quot;');
  const specs=bpExtractSpecs(p.title,cat.key);
  const badgeHtml=specs.length?`<div class="bp-spec-badges">${specs.map(s=>`<span class="bp-spec-badge bp-spec-badge--${s.cls}">${s.text}</span>`).join('')}</div>`:'';
  const imgSrc=img?img.replace(/\.(?:jpg|jpeg|png|gif|webp)(\?.*)?$/i,(m,q)=>m.replace(q||'','').replace(/\.(\w+)$/,'_400x400.$1')+(q||'')):'';
  const brandLabel=bpGuessBrand(p,cat.key);
  return `
    <div class="bp-pcard${isSel?' bp-pcard--selected':''}${!inStock?' bp-pcard--oos':''}" data-pid="${p.id}"
      onclick="bpPickComponent('${cat.key}',${pd},this)" role="button" tabindex="0"
      onkeydown="if(event.key==='Enter'||event.key===' ')this.click()">
      ${isSel?'<div class="bp-pcard-check">\u2713 selected</div>':''}
      ${imgSrc?`<img class="bp-pcard-img" src="${imgSrc}" alt="${alt}" loading="lazy">`:`<div class="bp-pcard-img-placeholder">${cat.icon}</div>`}
      <div class="bp-pcard-info">
        <div class="bp-pcard-brand">${brandLabel}</div>
        ${badgeHtml}
        <div class="bp-pcard-name" title="${p.title.replace(/"/g,'&quot;')}"
          onclick="event.stopPropagation();this.classList.toggle('bp-expanded')">${p.title}</div>
      </div>
      <div class="bp-pcard-bottom">
        <span class="bp-pcard-price">JOD ${price.toFixed(2)}</span>
        <button class="bp-pcard-btn${isSel?' bp-pcard-btn--selected':''}" type="button">${isSel?'\u2713':'+'}</button>
      </div>
    </div>`;
}

async function bpEnrichMetafields(cat){
  const q2=`{collection(handle:"${cat.col}"){products(first:48,sortKey:PRICE){edges{node{id
    brand:metafield(namespace:"compume",key:"brand_name"){value}
    cpu_socket:metafield(namespace:"compume",key:"cpu_socket"){value}
    tdp_watts:metafield(namespace:"compume",key:"tdp_watts"){value}
    form_factor:metafield(namespace:"compume",key:"form_factor"){value}
    ram_type:metafield(namespace:"compume",key:"ram_type"){value}
    ram_speed_mhz:metafield(namespace:"compume",key:"ram_speed_mhz"){value}
    psu_wattage:metafield(namespace:"compume",key:"psu_wattage"){value}
    cooler_type:metafield(namespace:"compume",key:"cooler_type"){value}
  }}}}}`;
  try{
    const r=await fetch(window.BP_API,{method:'POST',headers:{'Content-Type':'application/json','X-Shopify-Storefront-Access-Token':window.BP_STOREFRONT_TOKEN},body:JSON.stringify({query:q2})});
    const data=await r.json();
    if(data.errors){console.error('[BP:enrich:'+cat.key+']',JSON.stringify(data.errors,null,2));return;}
    const enriched=(data?.data?.collection?.products?.edges||[]).map(e=>e.node);
    let compat=false;
    enriched.forEach(ep=>{
      const mf={brand_name:ep.brand?.value||null,cpu_socket:ep.cpu_socket?.value||null,tdp_watts:ep.tdp_watts?.value?+ep.tdp_watts.value:null,form_factor:ep.form_factor?.value||null,ram_type:ep.ram_type?.value||null,ram_speed_mhz:ep.ram_speed_mhz?.value?+ep.ram_speed_mhz.value:null,psu_wattage:ep.psu_wattage?.value?+ep.psu_wattage.value:null,cooler_type:ep.cooler_type?.value||null};
      BP_MF_CACHE[ep.id]=mf;
      Object.keys(BP.build).forEach(k=>{if(BP.build[k]?.id===ep.id){Object.assign(BP.build[k],mf);compat=true;}});
    });
    if(compat)bpRunCompatCheck();
  }catch(e){console.warn('[BP] Metafield enrich failed:',e);}
}

function bpPickComponent(key,product,cardEl){
  if(BP_MF_CACHE[product.id])Object.assign(product,BP_MF_CACHE[product.id]);
  BP.build[key]=product;
  const grid=document.getElementById('bp-cat-'+key);
  if(grid){grid.querySelectorAll('.bp-pcard').forEach(c=>{
    const isSel=c.dataset.pid===product.id;
    c.classList.toggle('bp-pcard--selected',isSel);
    let ck=c.querySelector('.bp-pcard-check');
    if(isSel&&!ck){ck=document.createElement('div');ck.className='bp-pcard-check';ck.textContent='\u2713 selected';c.insertBefore(ck,c.firstChild);}else if(!isSel&&ck){ck.remove();}
    const btn=c.querySelector('.bp-pcard-btn');if(btn){btn.textContent=isSel?'\u2713':'+';btn.classList.toggle('bp-pcard-btn--selected',isSel);}
  });}
  const status=document.getElementById('bp-cat-status-'+key);
  if(status)status.innerHTML=`<span class="bp-cat-selected-badge">\u2713 ${product.title} \u2014 JOD ${(product.price||0).toFixed(2)}</span>`;
  bpRefreshBar();bpSaveToHash();bpRunCompatCheck();bpCheckFlashEligibility();
}

function bpRunCompatCheck(){
  const alerts=document.getElementById('bp-compat-alerts');if(!alerts)return;
  const b=BP.build;const errors=[],warnings=[],passes=[];
  if(b.cpu&&b.motherboard&&b.cpu.cpu_socket&&b.motherboard.cpu_socket){if(b.cpu.cpu_socket!==b.motherboard.cpu_socket)errors.push(`socket_match...... CPU ${b.cpu.cpu_socket} \u21ae MB ${b.motherboard.cpu_socket} [FAIL]`);else passes.push(`socket_match...... ${b.cpu.cpu_socket} \u2194 ${b.motherboard.cpu_socket} [PASS]`);}
  if(b.ram&&b.motherboard&&b.ram.ram_type&&b.motherboard.ram_type){if(b.ram.ram_type!==b.motherboard.ram_type)errors.push(`ram_type.......... RAM ${b.ram.ram_type} \u21ae MB supports ${b.motherboard.ram_type} [FAIL]`);else passes.push(`ram_type.......... ${b.ram.ram_type} [PASS]`);}
  if(b.psu&&b.cpu&&b.gpu&&b.psu.psu_wattage&&b.cpu.tdp_watts&&b.gpu.tdp_watts){const req=b.cpu.tdp_watts+b.gpu.tdp_watts+150;if(b.psu.psu_wattage<req)errors.push(`psu_adequacy...... ${b.psu.psu_wattage}W < required ${req}W [FAIL]`);else if(b.psu.psu_wattage<req+100)warnings.push(`psu_adequacy...... ${b.psu.psu_wattage}W meets minimum [WARNING]`);else passes.push(`psu_adequacy...... ${b.psu.psu_wattage}W \u2265 ${req}W [PASS]`);}
  if(b.case&&b.motherboard&&b.case.form_factor&&b.motherboard.form_factor){const cR=FF_RANK[b.case.form_factor]||0,mR=FF_RANK[b.motherboard.form_factor]||0;if(cR>0&&mR>0){if(mR>cR)errors.push(`form_factor....... Case ${b.case.form_factor} too small for ${b.motherboard.form_factor} [FAIL]`);else passes.push(`form_factor....... ${b.motherboard.form_factor} fits in ${b.case.form_factor} [PASS]`);}}
  if(b.cooler&&b.cooler.cooler_type&&b.cooler.cooler_type.startsWith('aio-'))warnings.push(`cooler_type....... AIO ${b.cooler.cooler_type.replace('aio-','')}mm \u2014 verify case radiator mount [WARNING]`);
  let html='';
  if(errors.length)html+=`<div class="bp-compat-banner error">&gt; [COMPAT_CHECK]\n${errors.map(e=>'&gt; '+e).join('\n')}</div>`;
  if(warnings.length)html+=`<div class="bp-compat-banner warn">&gt; [COMPAT_CHECK]\n${warnings.map(w=>'&gt; '+w).join('\n')}</div>`;
  if(passes.length&&!errors.length)html+=`<div class="bp-compat-banner ok">&gt; [COMPAT_CHECK]\n${passes.map(p=>'&gt; '+p).join('\n')}</div>`;
  if(!html&&b.cpu&&b.gpu&&b.ram)html='<div class="bp-compat-banner ok">&gt; [COMPAT_CHECK] \u2714 Compatible.</div>';
  alerts.innerHTML=html;
}

function bpRenderReview(){
  const container=document.getElementById('bp-stage-4');if(!container)return;
  const LABELS={cpu:'CPU',motherboard:'Motherboard',ram:'RAM',gpu:'GPU',psu:'PSU',storage:'Storage',case:'Case',cooler:'Cooler',fans:'Fans',thermalpaste:'Thermal Paste'};
  let sub=0;
  const rows=Object.entries(LABELS).map(([k,l])=>{const v=BP.build[k];if(!v)return '';sub+=v.price||0;return `<div class="bp-review-row"><span class="bp-review-row__cat">${l}</span><span class="bp-review-row__name">${v.title}</span><span class="bp-review-row__price">JOD ${(v.price||0).toFixed(2)}</span></div>`;}).filter(Boolean).join('');
  container.innerHTML=`
    <h2 class="bp-headline" style="font-size:clamp(1.8rem,4vw,3rem)">Review Your Build</h2>
    <p class="bp-subhead">Fine-tune add-ons. Then add to cart.</p>
    <div class="bp-review-list">${rows||'<p style="color:var(--bp-muted);padding:20px 0">No components yet. <a href="#" onclick="bpGoToStage(3);return false" style="color:var(--bp-cyan)">Pick your parts &rarr;</a></p>'}</div>
    <div id="bp-service-addons" style="margin-bottom:24px">
      <p class="bp-section-label" style="margin-bottom:16px">Service Add-ons</p>
      ${BP_SERVICES.map(s=>`<div style="margin-bottom:16px"><div style="font-family:var(--bp-font-display);font-weight:700;font-size:15px;text-transform:uppercase;letter-spacing:.12em;color:var(--bp-muted);margin-bottom:10px">${s.icon} ${s.label}</div><div id="bp-svc-${s.key}" class="bp-loading"><div class="bp-spinner"></div> Loading...</div></div>`).join('')}
    </div>
    <div id="bp-price-box"></div>
    <div style="display:flex;gap:12px;flex-wrap:wrap;margin-top:8px">
      <button class="bp-btn bp-btn--ghost" onclick="bpGoToStage(3)" type="button">&larr; Edit Parts</button>
      <button class="bp-btn bp-btn--primary" onclick="bpAddToCart()" type="button" id="bp-add-cart-btn">Add to Cart &amp; Checkout &rarr;</button>
    </div>`;
  bpRefreshPriceBox();BP_SERVICES.forEach(s=>bpLoadServiceSection(s));setTimeout(bpCheckFlashEligibility,800);
}
async function bpLoadServiceSection(svc){
  const el=document.getElementById('bp-svc-'+svc.key);if(!el)return;
  const q=`{collection(handle:"${svc.col}"){products(first:6){edges{node{id title priceRange{minVariantPrice{amount}} variants(first:1){edges{node{id}}}}}}}}}`;
  try{
    const r=await fetch(window.BP_API,{method:'POST',headers:{'Content-Type':'application/json','X-Shopify-Storefront-Access-Token':window.BP_STOREFRONT_TOKEN},body:JSON.stringify({query:q})});
    const data=await r.json();
    if(data.errors){console.error('[BP:svc:'+svc.key+']',JSON.stringify(data.errors,null,2));}
    const prods=(data?.data?.collection?.products?.edges||[]).map(e=>e.node);
    if(!prods.length){el.innerHTML='<span style="color:var(--bp-muted);font-size:14px;font-family:var(--bp-font-mono)">Not configured yet.</span>';return;}
    el.innerHTML=prods.map(p=>{
      const price=parseFloat(p.priceRange?.minVariantPrice?.amount||0);
      const varId=gidToNum(p.variants?.edges?.[0]?.node?.id);
      const pd=JSON.stringify({id:p.id,title:p.title,price,variantId:varId}).replace(/"/g,'&quot;');
      const isSel=BP.build[svc.key]?.id===p.id;
      return `<button class="bp-svc-pill${isSel?' active':''}" data-svc="${svc.key}" data-pid="${p.id}" onclick="bpPickService('${svc.key}',${pd},this)" type="button">${p.title} \u2014 JOD ${price.toFixed(2)}</button>`;
    }).join(' ');
  }catch(e){el.innerHTML=`<span style="color:var(--bp-red);font-family:var(--bp-font-mono);font-size:13px">> ${e.message}</span>`;}
}
function bpPickService(key,product,btn){
  const already=BP.build[key]?.id===product.id;
  if(already){BP.build[key]=null;document.querySelectorAll(`[data-svc="${key}"]`).forEach(b=>b.classList.remove('active'));}
  else{BP.build[key]=product;document.querySelectorAll(`[data-svc="${key}"]`).forEach(b=>b.classList.remove('active'));btn.classList.add('active');}
  bpRefreshPriceBox();bpRefreshBar();
}
function bpRefreshPriceBox(){
  const box=document.getElementById('bp-price-box');if(!box)return;
  let sub=0;
  ['cpu','motherboard','ram','gpu','psu','storage','case','cooler','fans','thermalpaste','os','warranty','build_fee'].forEach(k=>{const v=BP.build[k];if(v)sub+=v.price||0;});
  const pcCount=BP_REQUIRED.filter(k=>BP.build[k]).length;
  const auto3=pcCount>=5?+(sub*0.03).toFixed(2):0;
  const flash2=BP.flash.claimed?+(sub*0.02).toFixed(2):0;
  const total=+(sub-auto3-flash2).toFixed(2);
  box.innerHTML=`<div class="bp-price-breakdown">
    <div class="bp-price-line"><span>Subtotal (components + services)</span><span>JOD ${sub.toFixed(2)}</span></div>
    ${auto3>0?`<div class="bp-price-line discount"><span>\u2605 PC Builder Discount (3% off total)</span><span>&#8722;JOD ${auto3.toFixed(2)}</span></div>`:''}
    ${flash2>0?`<div class="bp-price-line discount bp-flash-discount-line"><span>\u26a1 Flash Bonus (2%)</span><span>&#8722;JOD ${flash2.toFixed(2)}</span></div>`:''}
    <div class="bp-price-line total"><span>Estimated Total</span><span class="amount">JOD ${total.toFixed(2)}</span></div>
  </div>
  ${pcCount>=5?'<div class="bp-compat-banner ok" style="margin-bottom:12px">&gt; [DISCOUNT_ACTIVE] 3% PC Builder discount applied automatically at checkout \u2014 no code needed.</div>':''}
  ${flash2>0?`<div class="bp-compat-banner flash" style="margin-bottom:16px">&gt; [FLASH_ACTIVE] Code ${BP.flash.code} \u2014 +2% bonus at checkout.</div>`:''}`;}
async function bpAddToCart(){
  const btn=document.getElementById('bp-add-cart-btn');
  if(btn){btn.disabled=true;btn.textContent='Adding to cart...';}
  const items=Object.entries(BP.build).filter(([,v])=>v&&v.variantId).map(([,v])=>({id:gidToNum(v.variantId),quantity:1})).filter(item=>item.id&&!isNaN(item.id));
  if(!items.length){alert('Please select at least one component.');if(btn){btn.disabled=false;btn.textContent='Add to Cart \u2192';}return;}
  console.log('[BP] Adding to cart:',items);
  const monitorLabel = BP_MONITOR_LABELS[BP.monitor_target] || BP.monitor_target || '';
  try{
    const r=await fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({
      items,
      attributes:{'Build Type':'Custom PC Builder','BP_UseCase':BP.useCase||'','BP_Budget':String(BP.budget),'BP_Monitor':monitorLabel,'BP_Resolution':BP.resolution||''}
    })});
    if(!r.ok){let errMsg='Cart add failed ('+r.status+')';try{const e=await r.json();errMsg=e.description||e.message||JSON.stringify(e);}catch(_){}console.error('[BP] /cart/add.js error:',errMsg);throw new Error(errMsg);}
    const added=await r.json();
    console.log('[BP] Cart add success:',added.items?.length,'items added');
    if(BP.flash.claimed&&BP.flash.code){window.location.href='/discount/'+BP.flash.code+'?redirect='+encodeURIComponent('/cart');}else{window.location.href='/cart';}
  }catch(e){if(btn){btn.disabled=false;btn.textContent='Add to Cart \u2192';}alert('Could not add to cart:\n'+e.message);}
}
function bpRefreshBar(){const count=BP_REQUIRED.filter(k=>BP.build[k]).length;const total=Object.values(BP.build).reduce((s,v)=>s+(v?v.price||0:0),0);const bc=document.getElementById('bp-bar-components');if(bc)bc.textContent=count;const bt=document.getElementById('bp-bar-total');if(bt)bt.textContent=total.toFixed(2);const cb=document.getElementById('bp-continue-btn');if(cb){let d=false;if(BP.stage===1&&!BP.useCase)d=true;if(BP.stage===2&&!BP.tier)d=true;cb.disabled=d;}}
function bpReset(){if(!confirm('Reset your build? All selections will be cleared.'))return;Object.keys(BP.build).forEach(k=>BP.build[k]=null);BP.useCase=null;BP.resolution='1080p';BP.monitor_target=null;BP.refresh_target=null;BP.monitor_tier=null;BP.tier=null;BP._shared_tier_id=null;if(BP.flash.timer)clearInterval(BP.flash.timer);BP.flash={offered:false,claimed:false,code:null,expiresAt:null,timer:null,msgCount:0,skipped:false};window.location.hash='';location.reload();}
function bpSaveToHash(){try{window.location.hash=btoa(JSON.stringify({s:BP.stage,u:BP.useCase,r:BP.resolution,mt:BP.monitor_target,b:BP.budget,t:BP.tier?BP.tier.id:null}));}catch(e){}}
function bpRestoreFromHash(){try{
  if(!window.location.hash)return;
  const h=window.location.hash.slice(1);
  if(new URLSearchParams(h).has('b'))return;
  const s=JSON.parse(atob(h));
  if(s.u)BP.useCase=s.u; if(s.r)BP.resolution=s.r; if(s.mt)BP.monitor_target=s.mt; if(s.b)BP.budget=s.b;
  const slider=document.getElementById('bp-budget-slider');
  if(slider&&s.b){slider.value=s.b;bpUpdateBudget(slider);}
  if(s.u){const btn=document.querySelector(`[data-use-case="${s.u}"]`);if(btn){btn.classList.add('bp-selected');btn.setAttribute('aria-pressed','true');}}
  if(s.mt){const btn=document.querySelector(`[data-monitor-target="${s.mt}"]`);if(btn){btn.classList.add('bp-selected');btn.setAttribute('aria-pressed','true');BP.resolution=btn.dataset.res||BP.resolution;BP.refresh_target=btn.dataset.refresh?parseInt(btn.dataset.refresh,10):null;BP.monitor_tier=btn.dataset.monitorTier||null;}}
  bpCheckWizardReady();
}catch(e){}}
function bpToggleAiPanel(){const aside=document.getElementById('bp-ai-aside'),fab=document.getElementById('bp-ai-fab');if(!aside)return;const open=aside.classList.toggle('bp-aside--open');if(fab)fab.setAttribute('aria-expanded',String(open));}
function bpAiTypewriter(div,log,text,speed){
  speed=speed||16;
  div.textContent='';
  var textNode=document.createTextNode('');
  var cursor=document.createElement('span');
  cursor.className='bp-ai-cursor';
  div.appendChild(textNode);
  div.appendChild(cursor);
  var i=0;
  function tick(){
    if(i>=text.length){
      var plain=textNode.textContent;
      div.innerHTML=bpColorize(plain)+'<span class="bp-ai-cursor"></span>';
      if(log)log.scrollTop=log.scrollHeight;
      bpCheckHandoff(text, log);
      return;
    }
    var ch=text[i];textNode.textContent+=ch;i++;
    if(log)log.scrollTop=log.scrollHeight;
    setTimeout(tick,ch==='\n'?100:speed);
  }
  tick();
}

/*
 * bpAiSend — v2.6
 * Action handler extended:
 *   set_component  — unchanged, populates BP.build slots
 *   finalize_build — new: validates variant IDs, calls /cart/add.js, redirects on success
 *   redirect_to_cart — new: alias for finalize_build (some Worker versions use this)
 * Defensive UI:
 *   If reply contains [BUILD_CONFIRMED] or [FINALIZE_BUILD_READY] but no finalize_build
 *   action is present, shows explicit "Cart action missing" message rather than silence.
 * [PICK:] detection:
 *   Warns in console if Worker emits product GIDs instead of variant GIDs.
 */
async function bpAiSend(){
  const input=document.getElementById('bp-ai-input');
  if(!input||!input.value.trim())return;
  const msg=input.value.trim();
  input.value='';
  bpAiAppend('user',msg);
  const tid='bp-ai-t-'+Date.now();
  bpAiAppend('bot','> [PROCESSING]\u25ae',tid);
  BP.aiMessages.push({role:'user',content:msg});
  BP.flash.msgCount++;
  try{
    const r=await fetch(BP.AI_URL,{
      method:'POST',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({
        messages:      BP.aiMessages,
        current_build: BP.build,
        budget_jod:    BP.budget,
        build_context: {
          use_case:       BP.useCase        || null,
          monitor_target: BP.monitor_target || null,
          resolution:     BP.resolution     || null,
          refresh_target: BP.refresh_target ?? null,
          monitor_tier:   BP.monitor_tier   || null,
          tier_id:        BP.tier?.id       || null,
        }
      })
    });
    const data=await r.json();
    const reply=data.message||'> [ERROR] No response.';
    bpAiReplace(tid,reply);
    BP.aiMessages.push({role:'assistant',content:reply});

    // ── Process actions from Worker ──────────────────────────────────────
    const log = document.getElementById('bp-ai-messages');
    let finalizeHandled = false;

    if(Array.isArray(data.actions)){
      data.actions.forEach(a=>{
        // Existing: set component in BP.build
        if(a.type==='set_component'&&a.key&&a.product){
          BP.build[a.key]=a.product;
        }
        // NEW: finalize_build — add all items to Shopify cart and redirect
        else if(a.type==='finalize_build'||a.type==='redirect_to_cart'){
          finalizeHandled = true;
          bpHandleFinalizeBuild(a, log);
        }
      });
    }

    // ── [PICK:] marker detection — warn on product GIDs ─────────────────
    // [PICK:gid://shopify/Product/123] cannot be added to cart (product, not variant)
    const pickMatches = reply.match(/\[PICK:([^\]]+)\]/g);
    if(pickMatches){
      pickMatches.forEach(m=>{
        const gid = m.replace('[PICK:','').replace(']','').trim();
        if(/gid:\/\/shopify\/Product\/\d+/.test(gid)){
          console.warn('[BP] [PICK:] received product GID (not variant):', gid);
        }
      });
    }

    // ── Defensive UI: marker present but no finalize action ──────────────
    // If AI says build is confirmed/ready but Worker sent no finalize_build action,
    // show an explicit message — do not pretend cart is ready.
    if(!finalizeHandled){
      const hasConfirmMarker = /\[BUILD_CONFIRMED\]|\[FINALIZE_BUILD_READY\]/.test(reply);
      if(hasConfirmMarker){
        bpAiAppend('bot',
          '> [CART_ACTION_MISSING] Cart action was not received from the server.\n' +
          '> Please use the \'Review Build \u2192\' button to add items to cart,\n' +
          '> or contact CompuMe for assistance.');
      }
    }

    bpRefreshBar();
  }catch(e){
    bpAiReplace(tid,`> [ERROR] CONNECTION_FAILED\n> ${e.message}`);
  }
  if(BP.flash.msgCount>=3)bpCheckFlashEligibility();
}

function bpAiAppend(role,text,id){const log=document.getElementById('bp-ai-messages');if(!log)return;const div=document.createElement('div');div.className=role==='bot'?'bp-ai-msg--bot':'bp-ai-msg--user';if(id)div.id=id;if(role==='user'){div.textContent=text;log.appendChild(div);log.scrollTop=log.scrollHeight;}else if(id){div.innerHTML=bpColorize(text)+'<span class="bp-ai-cursor"></span>';log.appendChild(div);log.scrollTop=log.scrollHeight;}else{log.appendChild(div);bpAiTypewriter(div,log,text,16);}}
function bpAiReplace(id,text){const el=document.getElementById(id);const log=document.getElementById('bp-ai-messages');if(el){el.id='';bpAiTypewriter(el,log,text,16);}else bpAiAppend('bot',text);}
function bpColorize(t){return t.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/\[PASS\]/g,'<span class="badge-pass">[PASS]</span>').replace(/\[FAIL\]/g,'<span class="badge-fail">[FAIL]</span>').replace(/\[WARNING\]/g,'<span class="badge-warn">[WARNING]</span>').replace(/\[OK\]/g,'<span class="badge-pass">[OK]</span>').replace(/\[ERROR\]/g,'<span class="badge-fail">[ERROR]</span>').replace(/\[CART_ERROR\]/g,'<span class="badge-fail">[CART_ERROR]</span>').replace(/\[CART_READY\]/g,'<span class="badge-pass">[CART_READY]</span>').replace(/\[CART_ACTION_MISSING\]/g,'<span class="badge-warn">[CART_ACTION_MISSING]</span>').replace(/\[ADDING_TO_CART\]/g,'<span class="badge-info">[ADDING_TO_CART]</span>').replace(/\[BUILD_CONFIRMED\]/g,'<span class="badge-pass">[BUILD_CONFIRMED]</span>').replace(/\[FINALIZE_BUILD_READY\]/g,'<span class="badge-pass">[FINALIZE_BUILD_READY]</span>').replace(/\[PROCESSING\]/g,'<span class="badge-info">[PROCESSING]</span>').replace(/\[COMPAT_CHECK\]/g,'<span class="badge-pass">[COMPAT_CHECK]</span>').replace(/\[FLASH_OFFER_DETECTED\]/g,'<span class="badge-warn">[FLASH_OFFER_DETECTED]</span>').replace(/\[FLASH_CLAIMED\]/g,'<span class="badge-pass">[FLASH_CLAIMED]</span>').replace(/\[FLASH_EXPIRED\]/g,'<span class="badge-fail">[FLASH_EXPIRED]</span>').replace(/\[DISCOUNT_ACTIVE\]/g,'<span class="badge-pass">[DISCOUNT_ACTIVE]</span>').replace(/\[SYSTEM_READY\]/g,'<span class="badge-pass">[SYSTEM_READY]</span>').replace(/\[SCANNING_INVENTORY\]/g,'<span class="badge-info">[SCANNING_INVENTORY]</span>').replace(/\[STATUS\]/g,'<span class="badge-info">[STATUS]</span>').replace(/\[CONTACT_HANDOFF_REQUESTED\]/g,'<span class="badge-warn">[CONTACT_HANDOFF_REQUESTED]</span>');}
function bpCheckFlashEligibility(){if(BP.flash.offered||BP.flash.claimed||BP.flash.skipped)return;const pcCount=BP_REQUIRED.filter(k=>BP.build[k]).length;const sub=Object.values(BP.build).reduce((s,v)=>s+(v?v.price||0:0),0);if(pcCount>=5&&sub>=500&&(BP.stage>=4||BP.flash.msgCount>=3))bpShowFlashOffer();}
async function bpShowFlashOffer(){BP.flash.offered=true;const payload={customerId:window.BP_CUSTOMER_ID||null,cartItems:BP_REQUIRED.filter(k=>BP.build[k]).length,cartTotal:Object.values(BP.build).reduce((s,v)=>s+(v?v.price||0:0),0),aiMessageCount:BP.flash.msgCount,stageReached:BP.stage};let expiresAt=new Date(Date.now()+30*60*1000).toISOString();try{const r=await fetch(BP.AI_URL+'flash/offer',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});const data=await r.json();if(!data.eligible){BP.flash.offered=false;return;}if(data.offerExpiresAt)expiresAt=data.offerExpiresAt;}catch(e){console.log('[BP Flash] dev mode');}BP.flash.expiresAt=expiresAt;bpRenderFlashOffer(expiresAt);}
function bpRenderFlashOffer(expiresAt){const log=document.getElementById('bp-ai-messages');if(!log)return;const card=document.createElement('div');card.className='bp-ai-msg--bot bp-flash-card';card.id='bp-flash-card';card.innerHTML=`&gt; <span class="badge-warn">[FLASH_OFFER_DETECTED]</span>\n&gt; ELIGIBILITY........... <span class="badge-pass">CONFIRMED</span>\n&gt; BONUS................. +2% off entire cart\n&gt; STACKS_WITH........... Auto 3% (total up to 5%)\n&gt; EXPIRES_IN............ <span class="bp-flash-timer" id="bp-flash-timer">30:00</span> <span class="bp-ai-cursor"></span>\n\n<div class="bp-flash-actions">\n  <button class="bp-flash-btn bp-flash-btn--claim" onclick="bpClaimFlash()" type="button">&gt; [ CLAIM ]</button>\n  <button class="bp-flash-btn bp-flash-btn--skip" onclick="bpSkipFlash()" type="button">&gt; [ SKIP ]</button>\n</div>`;log.appendChild(card);log.scrollTop=log.scrollHeight;const aside=document.getElementById('bp-ai-aside'),fab=document.getElementById('bp-ai-fab');if(aside&&window.innerWidth<1100&&!aside.classList.contains('bp-aside--open')){aside.classList.add('bp-aside--open');if(fab)fab.setAttribute('aria-expanded','true');}if(fab)fab.classList.add('bp-fab--pulse');bpStartFlashTimer(expiresAt);}
function bpStartFlashTimer(expiresAt){if(BP.flash.timer)clearInterval(BP.flash.timer);function tick(){const remaining=Math.max(0,new Date(expiresAt).getTime()-Date.now());const m=Math.floor(remaining/60000),s=Math.floor((remaining%60000)/1000);const display=String(m).padStart(2,'0')+':'+String(s).padStart(2,'0');document.querySelectorAll('.bp-flash-timer').forEach(el=>{el.textContent=display;if(remaining<=60000)el.classList.add('bp-flash-timer--urgent');else el.classList.remove('bp-flash-timer--urgent');});if(remaining<=0){clearInterval(BP.flash.timer);bpExpireFlash();}}tick();BP.flash.timer=setInterval(tick,1000);}
async function bpClaimFlash(){const claimBtn=document.querySelector('.bp-flash-btn--claim'),skipBtn=document.querySelector('.bp-flash-btn--skip');if(claimBtn){claimBtn.disabled=true;claimBtn.textContent='> [ PROCESSING... ]';}if(skipBtn)skipBtn.style.display='none';try{const r=await fetch(BP.AI_URL+'flash/claim',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({customerId:window.BP_CUSTOMER_ID||null,customerEmail:window.BP_CUSTOMER_EMAIL||null})});const data=await r.json();if(data.error)throw new Error(data.error);BP.flash.claimed=true;BP.flash.code=data.code;if(data.expiresAt)BP.flash.expiresAt=data.expiresAt;bpShowFlashClaimed(data.code,data.expiresAt||BP.flash.expiresAt);}catch(e){if(claimBtn){claimBtn.disabled=false;claimBtn.textContent='> [ CLAIM ]';}if(skipBtn)skipBtn.style.display='';bpAiAppend('bot',`> [ERROR] Flash claim failed.\n> ${e.message}`);}}
function bpShowFlashClaimed(code,expiresAt){const sub=Object.values(BP.build).reduce((s,v)=>s+(v?v.price||0:0),0);const savings=(sub*0.02).toFixed(2);const card=document.getElementById('bp-flash-card');if(card){card.innerHTML=`&gt; <span class="badge-pass">[FLASH_CLAIMED]</span>\n&gt; CODE.................. ${code}\n&gt; STATUS................ <span class="badge-pass">ACTIVE</span>\n&gt; EXPIRES_IN............ <span class="bp-flash-timer" id="bp-flash-timer">${bpTimerDisplay(expiresAt)}</span>\n&gt; SAVINGS............... &#8722;${savings} JOD\n&gt; TOTAL_DISCOUNT........ <span class="badge-pass">5% (3% auto + 2% flash)</span>\n\n<button class="bp-flash-btn bp-flash-btn--checkout" onclick="bpAddToCart()" type="button">&gt; [ PROCEED_TO_CHECKOUT ]</button>`;bpStartFlashTimer(expiresAt);}bpRefreshPriceBox();if(typeof bpTrack==='function')bpTrack('BP_Flash_Claimed',{code,savings_jod:parseFloat(savings)});}
function bpSkipFlash(){BP.flash.skipped=true;if(BP.flash.timer)clearInterval(BP.flash.timer);const card=document.getElementById('bp-flash-card');if(card)card.remove();}
function bpExpireFlash(){if(BP.flash.claimed)return;const card=document.getElementById('bp-flash-card');if(card)card.innerHTML=`&gt; <span class="badge-fail">[FLASH_EXPIRED]</span>\n&gt; The flash offer has expired.\n&gt; Your auto 3% discount still applies at checkout automatically.`;BP.flash.offered=false;}
function bpTimerDisplay(expiresAt){const remaining=Math.max(0,new Date(expiresAt).getTime()-Date.now());const m=Math.floor(remaining/60000),s=Math.floor((remaining%60000)/1000);return String(m).padStart(2,'0')+':'+String(s).padStart(2,'0');}
